import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  Activity,
  Zap,
  Sun,
  Moon,
  Menu,
  LogIn,
  Smartphone,
  Calculator,
  Beaker,
  Droplets,
  HeartPulse,
  Wind,
  CheckCircle2,
  Sparkles,
  MessageSquare,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/hooks/useTheme";
import { DrippleeLogo } from "@/components/DrippleeLogo";
import { BackgroundArt } from "@/components/BackgroundArt";
import { PwaInstallBanner } from "@/components/PwaInstallBanner";
import { FeedbackModal } from "@/components/FeedbackModal";

export default function HomePage() {
  const { user, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Smart cached session resolver for instant, zero-delay PWA rendering
  const hasCachedUser = typeof window !== "undefined" && !!localStorage.getItem("driplee_user");
  const showCalculator = !!user || (loading && hasCachedUser);

  if (user) {
    return <Navigate to="/calculator" replace />;
  }

  return (
    <div className="relative min-h-svh w-full overflow-x-hidden bg-slate-50/40 dark:bg-[#071118]/85 font-sans antialiased transition-colors duration-300">
      {/* Dynamic ambient art in background */}
      <BackgroundArt />

      {/* Navigation Header */}
      <header className="relative z-40 mx-auto max-w-5xl px-6 py-6 flex items-center justify-between">
        <div className="flex items-center">
          <DrippleeLogo size={36} showWordmark={true} />
        </div>
        <div className="flex items-center gap-6">
          <div className="hidden md:flex items-center gap-6 text-sm font-semibold text-[#0A3D5F] dark:text-[#EBF3F8]/90">
            <Link to="/docs" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Docs</Link>
            <Link to="/pricing" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Pricing</Link>
            <Link to="/about" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">About</Link>
            <Link to="/contact" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Contact</Link>
          </div>
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="home-menu-button"
              aria-label="Toggle navigation menu"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-[#0A3D5F]/10 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8] hover:border-[#0A3D5F]/20 dark:hover:border-white/20 active:scale-[0.97] transition-all"
              title="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMenuOpen(false)}
                />
                <div
                  data-testid="home-menu-dropdown"
                  className="absolute right-0 mt-2 w-48 rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl p-1.5 shadow-[0_12px_30px_rgba(0,0,0,0.1)] dark:shadow-[0_12px_30px_rgba(0,0,0,0.4)] z-50 animate-scale-up"
                >
                  {showCalculator ? (
                    <Link
                      to="/calculator"
                      onClick={() => setMenuOpen(false)}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8] hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-all"
                    >
                      <Activity className="h-4 w-4 text-[#085450] dark:text-[#36CDC5]" />
                      Calculator Suite
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setMenuOpen(false)}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8] hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-all"
                    >
                      <LogIn className="h-4 w-4 text-[#085450] dark:text-[#36CDC5]" />
                      Clinician Login
                    </Link>
                  )}

                  <Link
                    to="/install"
                    onClick={() => setMenuOpen(false)}
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8] hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-all"
                  >
                    <Smartphone className="h-4 w-4 text-[#085450] dark:text-[#36CDC5]" />
                    Installation Guide
                  </Link>

                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      setFeedbackOpen(true);
                    }}
                    aria-label="Leave Bedside Feedback"
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8] hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-all"
                  >
                    <MessageSquare className="h-4 w-4 text-[#085450] dark:text-[#36CDC5]" />
                    Leave Feedback
                  </button>

                  <button
                    onClick={() => {
                      toggleTheme();
                      setMenuOpen(false);
                    }}
                    aria-label="Toggle Night Shift / Day theme"
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5] hover:text-[#0A3D5F] dark:hover:text-[#EBF3F8] hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-all"
                  >
                    {theme === "night" ? (
                      <>
                        <Sun className="h-4 w-4 text-amber-400 fill-amber-400/10" />
                        <span>Switch to Day Mode</span>
                      </>
                    ) : (
                      <>
                        <Moon className="h-4 w-4 text-slate-700 dark:text-slate-300" />
                        <span>Switch to Night Shift</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 mx-auto max-w-5xl px-6 pt-10 pb-20 flex flex-col items-center text-center">
        {/* Subtle Pill Badge */}
        <div className="fade-up inline-flex items-center gap-2.5 rounded-full bg-[#0E7C76]/10 dark:bg-[#36CDC5]/10 border border-[#0E7C76]/25 dark:border-[#36CDC5]/20 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#085450] dark:text-[#36CDC5] mb-8 shadow-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-slow-ping absolute inline-flex h-full w-full rounded-full bg-[#0E7C76] dark:bg-[#36CDC5] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0E7C76] dark:bg-[#36CDC5]"></span>
          </span>
          <span>Infusion Rates & Clinical Math Calculators</span>
        </div>

        {/* Hero Headings — rendered immediately without animation opacity delay for instant LCP */}
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-[#0A3D5F] dark:text-[#EBF3F8] max-w-3xl leading-[1.1] mb-6">
          Calm, exact clinical calculations. <span className="font-serif italic font-normal bg-gradient-to-r from-[#085450] via-[#5F72BD] to-[#EC4899] dark:from-[#36CDC5] dark:via-[#818CF8] dark:to-[#EC4899] bg-clip-text text-transparent">Even at 3 a.m.</span>
        </h1>
        <p className="fade-up text-base sm:text-lg md:text-xl text-[#3E6179] dark:text-[#7CA1B5] max-w-2xl font-normal leading-relaxed mb-10">
          Driplee is a minimal, distraction-free assistant for ICU physicians, emergency doctors, and critical care nurses. <br />Fast and offline-ready tool. 
        </p>

        {/* Primary CTA Callout */}
        <div className="fade-up flex flex-col sm:flex-row items-center justify-center gap-4 w-full max-w-md mb-16">
          {showCalculator ? (
            <div className="relative w-full sm:w-auto">
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-[#2AA7A0] to-[#1C7F79] opacity-25 blur-md animate-pulse pointer-events-none"></div>
              <Link
                to="/calculator"
                className="relative w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#2AA7A0] to-[#1C7F79] hover:from-[#25948E] hover:to-[#176F69] text-white px-8 py-4 text-base font-bold shadow-xl shadow-[#2AA7A0]/20 dark:shadow-black/35 hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 animate-mild-pulse"
              >
                <Calculator className="h-5 w-5" />
                Open Calculator Suite
              </Link>
            </div>
          ) : (
            <>
              <div className="relative w-full sm:w-auto">
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-[#2AA7A0] to-[#1C7F79] opacity-25 blur-md animate-pulse pointer-events-none"></div>
                <Link
                  to="/login"
                  className="relative w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#2AA7A0] to-[#1C7F79] hover:from-[#25948E] hover:to-[#176F69] text-white px-8 py-4 text-base font-bold shadow-xl shadow-[#2AA7A0]/20 dark:shadow-black/35 hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 animate-mild-pulse"
                >
                  Access App
                </Link>
              </div>
              <span className="text-xs font-semibold text-[#3E6179] dark:text-[#7CA1B5]">Secure verification via Google</span>
            </>
          )}
        </div>

        <PwaInstallBanner variant="home-section" />

        {/* Core Value Pillars / Features */}
        <div className="fade-up w-full grid grid-cols-1 md:grid-cols-3 gap-6 text-left mb-16">
          {/* Card 1 */}
          <div className="rounded-[2rem] bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl border border-white/50 dark:border-white/5 p-7 shadow-[0_12px_40px_rgba(10,61,95,0.03)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.25)] hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(42,167,160,0.08)] dark:hover:shadow-[0_20px_50px_rgba(54,205,197,0.08)] hover:border-[#0E7C76]/30 dark:hover:border-[#36CDC5]/30 transition-all duration-300">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#0A3D5F]/10 to-[#2AA7A0]/10 dark:from-white/5 dark:to-cyan-500/10 flex items-center justify-center text-[#0A3D5F] dark:text-[#36CDC5] mb-5 shadow-inner">
              <Beaker className="h-5 w-5" />
            </div>
            <h3 className="font-serif text-lg font-semibold text-[#0A3D5F] dark:text-[#EBF3F8] mb-2">Infusion Drips & Vasopressors</h3>
            <p className="text-sm text-[#3E6179] dark:text-[#7CA1B5] leading-relaxed font-normal">
              Instant dose-to-rate conversions in mL/hr or mcg/min with dosage range buffers and custom drug concentration creator.
            </p>
          </div>

          {/* Card 2 */}
          <div className="rounded-[2rem] bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl border border-white/50 dark:border-white/5 p-7 shadow-[0_12px_40px_rgba(10,61,95,0.03)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.25)] hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(42,167,160,0.08)] dark:hover:shadow-[0_20px_50px_rgba(54,205,197,0.08)] hover:border-[#0E7C76]/30 dark:hover:border-[#36CDC5]/30 transition-all duration-300">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#0A3D5F]/10 to-[#2AA7A0]/10 dark:from-white/5 dark:to-cyan-500/10 flex items-center justify-center text-[#085450] dark:text-[#36CDC5] mb-5 shadow-inner">
              <Calculator className="h-5 w-5" />
            </div>
            <h3 className="font-serif text-lg font-semibold text-[#0A3D5F] dark:text-[#EBF3F8] mb-2">18 Clinical Math Tools</h3>
            <p className="text-sm text-[#3E6179] dark:text-[#7CA1B5] leading-relaxed font-normal">
              Pediatric & adult maintenance fluids, corrected Na+/Ca2+, Na+/K+ deficits, renal eGFR/CrCl and much more.
            </p>
          </div>

          {/* Card 3 */}
          <div className="rounded-[2rem] bg-white/70 dark:bg-slate-900/40 backdrop-blur-xl border border-white/50 dark:border-white/5 p-7 shadow-[0_12px_40px_rgba(10,61,95,0.03)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.25)] hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(42,167,160,0.08)] dark:hover:shadow-[0_20px_50px_rgba(54,205,197,0.08)] hover:border-[#0E7C76]/30 dark:hover:border-[#36CDC5]/30 transition-all duration-300">
            <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#0A3D5F]/10 to-[#2AA7A0]/10 dark:from-white/5 dark:to-cyan-500/10 flex items-center justify-center text-[#0A3D5F] dark:text-[#EBF3F8] mb-5 shadow-inner">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h3 className="font-serif text-lg font-semibold text-[#0A3D5F] dark:text-[#EBF3F8] mb-2">100% Ad-Free & Mobile-First</h3>
            <p className="text-sm text-[#3E6179] dark:text-[#7CA1B5] leading-relaxed font-normal">
              Zero advertisements, zero patient data logging. Optimized for quick one-handed mobile.
            </p>
          </div>
        </div>

        {/* Feature Grid Breakdown of 16 Clinical Calculators */}
        <div className="fade-up w-full max-w-5xl mx-auto mb-16 text-left">
          <div className="text-center mb-10">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#085450] dark:text-[#36CDC5]">
              Validated Clinical Formulas
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#0A3D5F] dark:text-[#EBF3F8] mt-1">
              Complete Critical Care Mathematics Suite
            </h2>
            <p className="text-xs sm:text-sm text-[#3E6179] dark:text-[#7CA1B5] mt-2 max-w-xl mx-auto font-normal">
              Every calculator includes step-by-step mathematical derivations, worked clinical examples, and peer-reviewed literature citations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Category 1 */}
            <div className="rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/50 p-5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#085450] dark:text-[#36CDC5] font-bold text-xs uppercase tracking-wider">
                <Beaker className="h-4 w-4" /> Infusions & Vasopressors
              </div>
              <ul className="text-xs text-[#0A3D5F] dark:text-[#EBF3F8] space-y-1.5 font-medium">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Continuous Drips (mL/hr & mcg/min)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Target Dose Buffers Guidance</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Custom Drug Concentration Formulator</li>
              </ul>
            </div>

            {/* Category 2 */}
            <div className="rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/50 p-5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#085450] dark:text-[#36CDC5] font-bold text-xs uppercase tracking-wider">
                <Droplets className="h-4 w-4" /> Pediatric & Maintenance Fluids
              </div>
              <ul className="text-xs text-[#0A3D5F] dark:text-[#EBF3F8] space-y-1.5 font-medium">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Holliday–Segar Method (Daily & Hourly)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> 4-2-1 Rule Weight-Based Fluid Estimator</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> IV Gravity Drop Rate Count (gtt/min)</li>
              </ul>
            </div>

            {/* Category 3 */}
            <div className="rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/50 p-5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#085450] dark:text-[#36CDC5] font-bold text-xs uppercase tracking-wider">
                <Activity className="h-4 w-4" /> Lab & Electrolyte Deficits
              </div>
              <ul className="text-xs text-[#0A3D5F] dark:text-[#EBF3F8] space-y-1.5 font-medium">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Corrected Na+ for Hyperglycemia</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Sodium & Potassium Deficit Estimators</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Free Water Deficit & Calcium Adjustment</li>
              </ul>
            </div>

            {/* Category 4 */}
            <div className="rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/50 p-5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#085450] dark:text-[#36CDC5] font-bold text-xs uppercase tracking-wider">
                <Calculator className="h-4 w-4" /> Renal Function & Metabolic
              </div>
              <ul className="text-xs text-[#0A3D5F] dark:text-[#EBF3F8] space-y-1.5 font-medium">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Cockcroft–Gault CrCl (mL/min)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> eGFR (CKD-EPI 2021 Equation)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Serum Anion Gap & Delta Ratio</li>
              </ul>
            </div>

            {/* Category 5 */}
            <div className="rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/50 p-5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#085450] dark:text-[#36CDC5] font-bold text-xs uppercase tracking-wider">
                <Wind className="h-4 w-4" /> Weaning Parameters
              </div>
              <ul className="text-xs text-[#0A3D5F] dark:text-[#EBF3F8] space-y-1.5 font-medium">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Rapid Shallow Breathing Index (RSBI)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Diaphragm Thickening Fraction (DTF)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Mechanical Ventilation Weaning Assessment</li>
              </ul>
            </div>

            {/* Category 6 */}
            <div className="rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/50 p-5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#085450] dark:text-[#36CDC5] font-bold text-xs uppercase tracking-wider">
                <HeartPulse className="h-4 w-4" /> Patient Metrics & Conversions
              </div>
              <ul className="text-xs text-[#0A3D5F] dark:text-[#EBF3F8] space-y-1.5 font-medium">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Body Surface Area (BSA - Mosteller)</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Ideal Body Weight vs Total Body Weight</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Multi-Unit Converter (mg, mcg, mL, mEq, mmol)</li>
              </ul>
            </div>

            {/* Category 7 */}
            <div className="rounded-2xl border border-[#0A3D5F]/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/50 p-5 space-y-2.5">
              <div className="flex items-center gap-2 text-[#085450] dark:text-[#36CDC5] font-bold text-xs uppercase tracking-wider">
                <Smartphone className="h-4 w-4" /> User Experience & Mobility
              </div>
              <ul className="text-xs text-[#0A3D5F] dark:text-[#EBF3F8] space-y-1.5 font-medium">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> 100% Ad-Free & Distraction-Free</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Fully Optimized for Mobile & One-Handed Use</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#085450] dark:text-[#36CDC5] shrink-0" /> Offline PWA Home-Screen Installable</li>
              </ul>
            </div>
          </div>
        </div>

        {/* SEO and Purpose Section */}
        <div className="fade-up w-full max-w-4xl mx-auto mt-8 mb-12 text-left rounded-[2rem] bg-gradient-to-br from-slate-50/70 to-white/90 dark:from-slate-900/20 dark:to-slate-900/40 border border-[#0A3D5F]/10 dark:border-white/5 p-8 sm:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-5">
              <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#085450] dark:text-[#36CDC5]">Clinical Utility Scope</span>
              <h2 className="font-serif text-2xl sm:text-3xl font-light text-[#0A3D5F] dark:text-[#EBF3F8] mt-2 mb-4 leading-snug">
                Purely a <span className="italic font-normal">mathematical</span> calculation assistant.
              </h2>
              <div className="h-1 w-12 bg-gradient-to-r from-[#0E7C76] to-cyan-500 rounded-full" />
            </div>
            
            <div className="lg:col-span-7 text-sm text-[#3E6179] dark:text-[#7CA1B5] space-y-4 font-normal leading-relaxed">
              <p>
                Driplee is built specifically to address the complex mathematical workflows encountered in critical care, anesthesia, patient care. It serves as a <strong>ICU drug calculator</strong>, <strong>IV rate calculator (mL/hr)</strong>, and <strong>clinical math suite</strong>, assisting clinicians and students in converting target intravenous dosages (utilizing a built-in <strong>mcg per kg per minute calculator</strong> or mcg/min) into volumetric pump rates.
              </p>
              <p>
                In addition to continuous vasoactive infusions, Driplee incorporates 18 peer-reviewed clinical math formulas.
              </p>
              <p>
                <strong>Important: Driplee does not provide medical opinions, clinical guidance, diagnoses, or treatment decisions.</strong> All templates exist solely to automate arithmetic conversions and are not clinical recommendations. Every therapeutic administration must be verified and authorized by a licensed healthcare professional.
              </p>
            </div>
          </div>
        </div>

        {/* Small Medical Disclaimer */}
        <div className="fade-up max-w-xl text-center border-t border-[#0A3D5F]/10 dark:border-white/10 pt-8">
          <p className="text-xs text-[#3E6179] dark:text-[#7CA1B5] leading-relaxed font-normal">
            <strong>Medical Disclaimer:</strong> Driplee is a calculation assistant built exclusively for qualified healthcare professionals. It is not a substitute for clinical judgment. Always verify all rates, doses, and concentrations against hospital protocols before patient administration.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#0A3D5F]/10 dark:border-white/10 bg-white/60 dark:bg-slate-950/20 backdrop-blur-md py-10 text-center text-[#3E6179] dark:text-[#7CA1B5]">
        <div className="mx-auto max-w-5xl px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs font-normal">
            © 2026 Driplee · Developed and regulated under the laws of <strong>India</strong>.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-xs font-semibold">
            <Link to="/about" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">About</Link>
            <span>·</span>
            <Link to="/contact" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Contact</Link>
            <span>·</span>
            <Link to="/docs" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Docs</Link>
            <span>·</span>
            <Link to="/methodology" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Methodology</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-[#085450] dark:hover:text-[#36CDC5] transition">Terms of Service</Link>
          </div>
        </div>
      </footer>

      <FeedbackModal
        isOpen={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
    </div>
  );
}

